import Highcharts from 'highcharts';
import {format} from '../../filters/format';

export default class Chart {
  constructor(config = {}){
    let defaults = {
      options: {
        tooltip: {}
      },
      yAxis: {
        labels: {}
      }
    };
    this.config = _.merge({}, defaults, config);
  }

  set seriesLimit(value) {
    this.config.options.seriesLimit = parseInt(value||'0');
  }

  get seriesLimit() {
    return this.config.options.seriesLimit;
  }

  set series(series) {
    this.config.series = _.flatten(series.map((serie, index) => {
      return this.processSerie(serie, index, series.length);
    }));
  }

  setDrilldown(drilldownConfig={}) {
    this.config.options.drilldown = drilldownConfig;
  }

  processSerie({overrides, rows, summary}, index, seriesLength) {
    overrides = overrides || {};
    let serieCopy = overrides.serieCopy;
    delete overrides.serieCopy;

    let serie = _.merge({
      data: [],
      lineWidth: 3,
      fillColor: Highcharts.getOptions().colorsGradient[index],
      color: Highcharts.getOptions().colors[index]
    }, _.omit(overrides, 'metric', 'primary'));

    // disable serie toggle with legend click when single serie
    if (seriesLength <= 1) {
      serie.events = {
        legendItemClick: function() { return false; }
      }
    }

    let isXAxisTypeCategory = _.get(this.config, 'xAxis[0].type') == 'category';
    let xAxisPropName = (isXAxisTypeCategory) ? 'name' : 'x';

    if (!overrides.noGraph && rows.length) {
      serie.data = rows.map((row) => {
        row[xAxisPropName] = isXAxisTypeCategory ? row.key : moment.utc(row.key).toDate();
        row.y = row.value;
        return row;
      });

      if(!isXAxisTypeCategory) {
        this.setSerieDateFormat(serie);
      }
    }

    // comparison serie needs to be aligned to 2nd xAxis
    if (overrides.comparison) {
      serie.xAxis = 1;
    }

    if (this.seriesLimit && index >= this.seriesLimit) {
      serie.visible = false;
    }

    if (serie.subType == 'donut') {
      this.setDonutStyling(serie);
    }

    this.setSerieMarker(serie);

    if (overrides.decorateSerie) {
      overrides.decorateSerie(serie);
    }

    if (serieCopy) {
      return [serie, this.buildSerieCopy({serie,serieCopy})];
    } else {
      return serie;
    }
  }

  buildSerieCopy({serie, serieCopy}) {
    return _.merge({},serie,serieCopy, {
      color: '#454545',
      events: {
        legendItemClick: function() { return false; },
      },
      visible: false
    });
  }

  setComparison(value) {
    this.config.options.comparison = !!value;
  }

  setSerieDateFormat(serie) {
    let firstDate = _.get(serie.data, '[0].x');
    let secondDate = _.get(serie.data, '[1].x');
    let hoursDiff = moment(secondDate).diff(firstDate, 'hours');

    serie.pointStart = firstDate;
    if (firstDate && secondDate && hoursDiff < 24) {
      serie.dateFormat = 'datetime';
      serie.pointInterval = 1 * 60 * 60;
    } else {
      serie.pointInterval = 24 * 60 * 60;
    }
  }

  setDonutStyling(serie) {
    _.merge(serie, {
      borderColor: null,
      innerSize: '55%',
      size: '100%',
      showInLegend: true,
      dataLabels: {
        enabled: false
      }
    });
  }

  setSerieMarker(serie) {
    if (serie.data.length == 1) {
      serie.animation = false;
      serie.marker = {
        enabled: true,
        radius: 5,
        symbol: 'circle'
      }
    }
  }

  setTooltipType(value) {
    this.config.options.tooltipType = value;
  }

  setYAxis(series, forceSingleYAxis, isLogarithmic, customYAxis) {
    this.config.yAxis = [];
    let yAxisTemplate = {};
    let getYAxisObject = () => {
      return { title: { text: null },
              labels: {
                style: { color: '#000000', font: '600 12px "proxima-nova", sans-serif' }
              },
              gridLineDashStyle: 'Dash',
              type: isLogarithmic ? 'logarithmic' : 'linear'
            };
    };

    let formatYAxis = (yAxisTemplate, serie, index) => {
      yAxisTemplate.labels.style.color = this.config.forceGrayYAxis ? '#b6b6b6' : Highcharts.getOptions().colors[index];
      yAxisTemplate.labels.formatter = function () {
        return format(this.value, serie.overrides.format);
      };
    }

    if(forceSingleYAxis) {
      yAxisTemplate = getYAxisObject();
      yAxisTemplate.labels.style.color = '#b6b6b6';
      yAxisTemplate.labels.formatter = function () {
        return format(this.value, series[0].overrides.format);
      };
      this.config.yAxis.push(yAxisTemplate);
    } else if(customYAxis){
      _.forEach(series, (serie, index) => {
        yAxisTemplate = getYAxisObject(yAxisTemplate, serie, index);
        formatYAxis(yAxisTemplate, serie, index);
        // assuming customYAxesIndex of series (i) <= customYAxesIndex of series (i + 1)
        if(serie.overrides.customYAxesIndex + 1 > this.config.yAxis.length) {
          this.config.yAxis.push(yAxisTemplate);
        }
        serie.overrides.yAxis = serie.overrides.customYAxesIndex;
      });
    } else {
      _.forEach(series, (serie, index) => {
        yAxisTemplate = getYAxisObject();
        formatYAxis(yAxisTemplate, serie, index);
        this.config.yAxis.push(yAxisTemplate);
        serie.overrides.yAxis = index;
      });
    }

    this.updateSerieYAxisOpposite(this.config.yAxis, series);
  }

  updateSerieYAxisOpposite(yAxisArray, series) {
    let howManyShowYAxis = 0;
    let showYAxis = false;
    let showOpposite = false;
    _.forEach(yAxisArray, (yAxis, index) => {
      let serieComparison = (index) => (series[index].overrides || series[index].options).comparison;
      let serieVisible = (index) => (series[index].overrides || series[index]).visible;
      let comparison = serieComparison(index) === true;
      let visible = !(serieVisible(index) === false);
      let previousVisible = index > 0 && !(serieVisible(index-1) === false);

      if (howManyShowYAxis < 2 && visible && (!comparison || !previousVisible)) {
        showYAxis = true;
        showOpposite = howManyShowYAxis > 0 ? true : false;
        howManyShowYAxis++;
      } else {
        showYAxis = false;
      }

      if (comparison) {
        if (yAxis.update) {
          // dont show comparison yAxis if all values are 0
          showYAxis = showYAxis && yAxis.dataMax != 0 && yAxis.dataMin != 0;
        } else {
          // set yAxis min/max to ensure yAxis 0 value is at chart bottom
          // http://stackoverflow.com/questions/13991873/highcharts-ensure-y-axis-0-value-is-at-chart-bottom
          let values = _.map(series[index].rows, 'value');
          if (values.length && _.max(values) == 0 && _.min(values) == 0) {
            yAxis.min = 0;
            yAxis.max = 5;
          }
        }
      }

      if (yAxis.update) {
        yAxis.update({
          labels: {
            enabled: showYAxis
          },
          opposite: showOpposite
        });
      } else {
        yAxis.labels.enabled = showYAxis;
        yAxis.opposite = showOpposite;
      }
    });
  }

  setEventHandlers(series) {
    let _this = this;

    _.forEach(series, (serie) => {
      serie.overrides.events = {
        hide: function () {
          $(this.legendGroup.div).find('.c-chart-legend__item').addClass('is-faded');
          _this.updateSerieYAxisOpposite(this.chart.yAxis, this.chart.series);
        },
        show: function () {
          $(this.legendGroup.div).find('.c-chart-legend__item').removeClass('is-faded');
          _this.updateSerieYAxisOpposite(this.chart.yAxis, this.chart.series);
        }
      }
      if(serie.events) {
        _.assign(serie.overrides.events, serie.events);
      }
    });
  }

  setTooltipShare(share) {
    this.config.options.tooltip.shared = !!share;
  }

  renderIcon(iconsNamePrefix, value) {
    return '<svg role="img" class="o-icon o-icon--' + iconsNamePrefix + '">' +
    '<use xlink:href="#' + iconsNamePrefix + '-' + value.toLowerCase() + '"></use>' +
    '</svg>';
  }

  buildStyledXAxis(xAxis, useIcons, iconsNamePrefix) {
    let customXAxis = {};
    let _this = this;

    if(useIcons == true) {
      customXAxis = {
        labels: {
          style: {
            color: '#b6b6b6'
          },
          useHTML: true,
          formatter: function()  {
            var icon = _this.renderIcon(iconsNamePrefix, this.value);
            return icon;
          }
        }
      };
    } else {
      customXAxis = {
        labels: {
          rotation: _.get(this.config, 'xAxisLabelsRotation', -45),
          style: {
            color: '#b6b6b6',
            font: '600 12px "proxima-nova", sans-serif'
          }
        },
        minPadding: 0,
        maxPadding: 0
      };
    }

    return _.merge(customXAxis, xAxis, this.config.options.xAxis || {});
  }

  buildComparisonXAxis() {
    return {
      type: 'datetime',
      opposite: true,
      labels: {
        enabled: false
      },
      lineWidth: 0,
      tickWidth: 0,
      minPadding: 0,
      maxPadding: 0
    };
  }

  setXAxisCategories(series) {
    let categories = _.chain(series).map('rows').flatten().map('key').uniq().value();

    this.config.xAxis = [
      this.buildStyledXAxis({
        type: 'category',
        categories
      },
      this.config.useIconsForXAxisLabels,
      this.config.iconsNamePrefix),
      this.buildComparisonXAxis()
    ];
  }

  setXAxisDates() {
    this.config.xAxis = [
      this.buildStyledXAxis({
        type: 'datetime',
        dateTimeLabelFormats: {
          hour: '%H:%M',
          day: '%e.%m.%Y',
          week: '%e.%m.%Y',
          month: '%e.%m.%Y'
        }
      }),
      this.buildComparisonXAxis()
    ];
  }

  get isEmpty() {
    if (!this.config.series) {
      return false;
    } else {
      return this.config.series.filter(s => s.data.length).length == 0;
    }
  }
}


/** WEBPACK FOOTER **
 ** ./src/common/analyticsPanel/chart.js
 **/